import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Attributes } from '../../../interfaces/services';
import { AuthService } from '../../../services/auth.service';
import { Roles } from '../../../utils/roles';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ClientSelectorComponent } from '../../../components/client-selector/client-selector.component';
import { InputComponent } from '../../../components/input/input.component';
import { UploadComponent } from '../../../components/upload/upload.component';
import { Client } from '../../../interfaces/client';
import { EmployeeSelectorComponent } from '../../../components/employee-selector/employee-selector.component';
import { User } from '../../../interfaces/user';
import { Budget } from '../../../interfaces/budget';
import { MediaViewerComponent } from '../../../components/media-viewer/media-viewer.component';
import { MediaFile } from '../../../interfaces/media_file';

export interface ServiceData {
  serviceData: Attributes;
  files: File[];
}
@Component({
  selector: 'app-add-service',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    InputTextModule,
    InputTextareaModule,
    ReactiveFormsModule,
    ButtonModule,
    DividerModule,
    ClientSelectorComponent,
    UploadComponent,
    CalendarModule,
    InputNumberModule,
    InputComponent,
    EmployeeSelectorComponent,
    MediaViewerComponent,
  ],
  templateUrl: './add-service.component.html',
})
export class AddServiceComponent implements OnInit, OnChanges {
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() save = new EventEmitter<ServiceData>();
  @Output() isBudgetChange = new EventEmitter<boolean>();
  @Input() budgetData!: Budget;
  @ViewChild(UploadComponent) uploadComponent!: UploadComponent;

  @Input()
  set visible(value: boolean) {
    this._visible = value;
    if (this._visible && this.budgetData) {
      this.initializeFormWithBudget();
    }
  }
  get visible(): boolean {
    return this._visible;
  }

  private readonly _authService = inject(AuthService);
  private _selectedFiles: File[] = [];
  private _fb = inject(FormBuilder);
  protected formService!: FormGroup;
  private _visible = false;

  protected isBudget = signal<boolean>(false);
  protected isAdmin = signal<boolean>(false);
  protected mediaFiles = signal<MediaFile[]>([]);

  ngOnInit(): void {
    this.loadForm();
    this._authService.getRoleObservable().subscribe((role) => {
      this.isAdmin.set(String(role) === Roles.ADMIN);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budgetData'] && changes['budgetData'].currentValue) {
      this.initializeFormWithBudget();
    }
  }

  private initializeFormWithBudget(): void {
    if (!this.budgetData?.attributes) {
      return;
    }

    this.isBudget.set(true);
    const {
      client,
      description,
      startAt,
      import: budgetImport,
      media,
    } = this.budgetData.attributes;

    if (media?.data?.length) {
      this.mediaFiles.set(media.data);
    }

    this.formService.patchValue({
      client: client?.data?.id || null,
      name:
        client?.data?.attributes?.name || this.budgetData.attributes.name || '',
      email:
        client?.data?.attributes?.email ||
        this.budgetData.attributes.email ||
        '',
      phone:
        client?.data?.attributes?.phone ||
        this.budgetData.attributes.phone ||
        '',
      phone_optional:
        client?.data?.attributes?.phone_optional ||
        this.budgetData.attributes.phone_optional ||
        '',
      province:
        client?.data?.attributes?.province ||
        this.budgetData.attributes.province ||
        '',
      city:
        client?.data?.attributes?.city || this.budgetData.attributes.city || '',
      address:
        client?.data?.attributes?.address ||
        this.budgetData.attributes.address ||
        '',
      postal_code:
        client?.data?.attributes?.postal_code ||
        this.budgetData.attributes.postal_code ||
        '',
      nif_cif:
        client?.data?.attributes?.nif_cif ||
        this.budgetData.attributes.nif_cif ||
        '',
      description: description || '',
      startAt: startAt ? new Date(startAt) : '',
      import: budgetImport || null,
    });
    this.formService.disable();
  }

  private loadForm(): void {
    this.formService = this._fb.group({
      client: [null],
      email: [''],
      name: [''],
      phone: [''],
      phone_optional: [''],
      province: [''],
      city: [''],
      address: [''],
      postal_code: [''],
      nif_cif: [''],
      description: [''],
      startAt: [''],
      import: [null],
      users_permissions_users: [null],
    });
  }

  closeDialog(): void {
    //this.visible = false;
    this.visibleChange.emit(false);
    this.formService.reset();
    this.formService.enable();
    this._selectedFiles = [];
    this.uploadComponent.clearFiles();
  }

  onSubmit(): void {
    const serviceData = this.formService.getRawValue();
    Object.keys(serviceData).forEach((key) => {
      if (serviceData[key] === '') {
        serviceData[key] = null;
      }
    });

    this.save.emit({
      serviceData,
      files: this._selectedFiles,
    });

    this.closeDialog();
  }

  onFilesSelected(files: File[]): void {
    this._selectedFiles = files;
  }

  onClientSelected(selectedClient: Client): void {
    if (!selectedClient) {
      this.formService.reset();
      this.formService.enable({ emitEvent: false });
      return;
    } else {
      this.formService.disable({ emitEvent: false });
      this.formService.get('startAt')?.enable();
      this.formService.get('description')?.enable();
      this.formService.get('import')?.enable();
      this.updateFormValues(selectedClient);
    }
  }

  onEmployeeSelected(employeesIds: User[]): void {
    this.formService.patchValue({ users_permissions_users: employeesIds });
  }

  private updateFormValues(client: Client): void {
    const {
      name,
      email,
      phone,
      phone_optional,
      province,
      city,
      address,
      postal_code,
      nif_cif,
    } = client.attributes;
    this.formService.patchValue({
      client: client.id,
      name,
      email,
      phone,
      phone_optional,
      province,
      city,
      address,
      postal_code,
      nif_cif,
    });
  }
}
